import { shouldAnimate, transition } from '@helpers/animate';
import { imageCoverContainer } from '@helpers/aspectRatio';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { baseGrid } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import { h1, paragraphLarge } from '@helpers/typography';
import svgLogoDesktop from '@img/misc/inner-hero-logo-desktop.svg';
import svgLogoMobile from '@img/misc/inner-hero-logo-mobile.svg';
import SectionWrapper from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import styled, { css } from 'styled-components';
const Wrapper = styled.div `
  overflow: hidden;
  overflow: clip;
  position: relative;
`;
const Container = styled(SectionWrapper) `
  position: relative;
  background-color: var(--bgColor);
  color: var(--fgColor);
  margin: 0;
  padding: 0;

  &[data-theme='light'] {
    --bgColor: ${brand.white};
    --fgColor: ${brand.black};
  }

  &[data-theme='dark'] {
    --bgColor: ${brand.black};
    --fgColor: ${brand.white};
  }

  &[data-logo='true'] {
    background-repeat: no-repeat;
    background-size: contain;

    @media ${until(Device.Tablet)} {
      background-image: url('${svgLogoMobile}');
      background-position: center right;
    }

    @media ${from(Device.Tablet)} {
      background-image: url('${svgLogoDesktop}');
      background-position: center left;
    }
  }
`;
const Inner = styled.div `
  ${baseGrid};

  ${Container}[data-image="true"] & {
    min-height: calc(632 / 1920 * 100vw);

    @media ${from(Device.Tablet)} {
      grid-template-rows: 1px auto;
    }
  }
`;
const BreadcrumbWrapper = styled.div `
  ${transition('opacity')}

  ${Container}[data-image="false"] & {
    margin-top: ${fluid(15, 30)};
    margin-bottom: ${fluid(28, 60)};
  }

  ${Container}[data-image="true"] & {
    grid-row: 1 / span 1;
    margin-top: 12px;

    @media ${until(Device.Tablet)} {
      grid-column: 1 / span 12;
      margin-bottom: 16px;
    }

    @media ${from(Device.Tablet)} {
      grid-column: 1 / span 5;
      margin-bottom: calc((100% + 12px) * -1);
    }

    @media ${from(Device.DesktopSmall)} {
      grid-column: 2 / span 5;
    }
  }

  ${shouldAnimate(css `
    ${Container}[data-animate="false"] & {
      opacity: 0;
    }

    ${Container}[data-animate="true"] & {
      opacity: 1;
    }
  `)}
`;
const ContentWrapper = styled.div `
  @media ${until(Device.Tablet)} {
    grid-column: 1 / span 12;
    grid-row: 2;
  }

  @media ${from(Device.Tablet)} {
    display: flex;
    flex-direction: column;
    grid-column: 1 / span 5;
    grid-row: 2;
    justify-content: center;
    padding: var(--sectionSpacing) 0;

    ${Container}[data-image="false"] & {
      grid-column: 1 / span 12;
      padding: 0 0 calc(var(--sectionSpacing) / 2);

      &[data-reduced-layout='true'] {
        padding: 0;
      }
    }
  }

  @media ${from(Device.DesktopSmall)} {
    grid-column: 2 / span 5;

    ${Container}[data-image="false"] & {
      grid-column: 1 / span 12;

      &[data-reduced-layout='true'] {
        padding: 0;
      }
    }
  }
`;
const Title = styled.h1 `
  ${transition('opacity')}

  ${h1}

  max-width: 421px;

  ${Container}[data-theme='dark'] & {
    span {
      color: ${brand.yellow};
    }
  }

  ${Container}[data-image="false"] & {
    max-width: none;
  }

  ${Container}[data-image="false"][data-title-size="large"] & {
    font-size: ${fluid(45, 140)};
    line-height: ${fluid(45, 140)};
  }
  
  ${Container}[data-image="true"][data-title-size="large"] & {
    font-size: ${fluid(45, 140)};
    line-height: ${fluid(45, 140)};
  }

  ${shouldAnimate(css `
    ${Container}[data-animate="false"] & {
      opacity: 0;
    }

    ${Container}[data-animate="true"] & {
      opacity: 1;
    }
  `)}
`;
const Content = styled.p `
  ${transition('opacity')}

  ${paragraphLarge}

  max-width: 663px;

  @media ${from(Device.Tablet)} {
    margin: 0;
  }

  ${shouldAnimate(css `
    transition-delay: 0.3s;

    ${Container}[data-animate="false"] & {
      opacity: 0;
    }

    ${Container}[data-animate="true"] & {
      opacity: 1;
    }
  `)}
`;
const ImageWrapper = styled.div `
  @media ${until(Device.Tablet)} {
    display: none;
  }

  @media ${from(Device.Tablet)} {
    position: absolute;
    top: 0;
    left: calc(50% + (var(--gutterWidth) * 0.5));
    width: calc(50% + (var(--gutterWidth) * -0.5));
    height: 100%;
  }
`;
const Image = styled.img `
  ${transition('filter, opacity')}

  ${imageCoverContainer()}

  ${shouldAnimate(css `
    transition-duration: 1s;

    ${Container}[data-animate="false"] & {
      filter: grayscale(0.5);
      opacity: 0;
    }

    ${Container}[data-animate="true"] & {
      filter: grayscale(0);
      opacity: 1;
    }
  `)}
`;
const BackgroundLogo = styled.span `
  --size: calc(670 / 1920 * 100vw);

  ${transition('opacity, transform', 1.5)}

  position: absolute;
  right: 0;
  top: 50%;
  display: block;
  color: ${brand.yellow};
  height: var(--size);
  transform: translateX(50%) translateY(-50%) rotate(-150deg);
  transform-origin: center center;
  width: var(--size);

  ${shouldAnimate(css `
    [data-animate='false'] & {
      opacity: 0;
      transform: translateX(50%) translateY(-50%) rotate(-180deg);
    }

    [data-animate='true'] & {
      opacity: 1;
      transform: translateX(50%) translateY(-50%) rotate(-150deg);
    }
  `)}

  @media ${until(Device.Tablet)} {
    display: none;
  }
`;
const BannerWrapper = styled.div `
  position: relative;
  height: ${fluid(300, 600)};
  overflow: hidden;
  overflow: clip;

  ${Wrapper}[data-banner-layout="fixed"] & {
    clip-path: inset(0px);
  }
`;
const Banner = styled.img `
  ${transition('filter, opacity')}

  ${imageCoverContainer()}

  ${shouldAnimate(css `
    transition-duration: 1s;

    ${Container}[data-animate="false"] & {
      filter: grayscale(0.5);
      opacity: 0;
    }

    ${Container}[data-animate="true"] & {
      filter: grayscale(0);
      opacity: 1;
    }
  `)}

  ${Wrapper}[data-banner-layout="fixed"] & {
    position: fixed;
    z-index: -1;
  }
`;
const Video = styled.video `
  ${imageCoverContainer()}

  ${Wrapper}[data-banner-layout="fixed"] & {
    position: fixed;
    z-index: -1;
  }
`;
const InnerPageHeroStyles = {
    BackgroundLogo,
    Banner,
    BannerWrapper,
    BreadcrumbWrapper,
    Container,
    Content,
    ContentWrapper,
    Image,
    ImageWrapper,
    Inner,
    Title,
    Wrapper,
    Video
};
export default InnerPageHeroStyles;
